<script lang="ts" setup>
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import useMediaQuery from '@leaflink/stash/useMediaQuery';
  import { storeToRefs } from 'pinia';
  import { RouterLink } from 'vue-router';

  import { ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';

  const isExtraLargeScreen = useMediaQuery('screen and (min-width: 1321px)');
  const companyStore = useCompanyStore();
  const { activeCompany } = storeToRefs(companyStore);
  const { getIsSplitEnabled } = storeToRefs(useUserStore());

  const emit = defineEmits<{
    (e: 'navigate'): void;
    (e: 'toggle'): void;
  }>();
</script>

<template>
  <div :class="['flex align-middle', $style.wrapper]">
    <Button v-if="!isExtraLargeScreen" data-test="button|navToggle" icon :class="$style.toggle" @click="emit('toggle')">
      <Icon name="menu" />
    </Button>

    <component
      :is="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? RouterLink : 'a'"
      :class="$style.link"
      :href="activeCompany ? `/c/${activeCompany.slug}/` : 'javascript:void(0)'"
      :to="{ name: ROUTE_NAMES.COMPANY_DASHBOARD, params: { company: activeCompany?.slug } }"
      @click="emit('navigate')"
    >
      <svg fill="none" viewBox="0 0 128 22" xmlns="http://www.w3.org/2000/svg" :class="[$style.logo, 'block']">
        <g clip-path="url(#logo-clip)">
          <path
            fill="currentColor"
            d="M32.326.063h3.56V22h-3.56V.063zM52.787 15.77H41.702c.397 1.995 1.935 3.008 4.054 3.008 1.565 0 3.04-.614 3.9-1.779l2.364 1.81C50.7 20.896 48.18 22 45.57 22c-4.298 0-7.553-3.007-7.553-7.486 0-4.572 3.44-7.458 7.523-7.458 4.115 0 7.34 2.856 7.34 7.334-.005.462-.035.923-.093 1.38zm-3.47-2.484c-.215-1.964-1.72-3.1-3.715-3.1-1.935 0-3.47.921-3.9 3.1h7.615zM70.242 7.364V22h-3.563v-2.24C65.698 21.294 63.67 22 61.917 22c-3.777 0-7.066-2.915-7.066-7.486 0-4.602 3.255-7.458 7.033-7.458 1.811 0 3.81.707 4.79 2.21V7.362l3.569.002zm-3.624 7.119c0-2.517-2.088-4.112-4.145-4.112-2.21 0-4.023 1.718-4.023 4.112 0 2.393 1.812 4.172 4.023 4.172 2.18 0 4.145-1.656 4.145-4.172zM76.997 6.413v1.258h3.194v2.856h-3.194V22h-3.559V6.597C73.438 2.485 75.4 0 79.393 0h1.69l-.984 3.314c-2.427 0-3.133 1.35-3.102 3.1zM83.584.065h3.561V22h-3.561V.065zM89.827 2.608C89.827 1.35 90.902.43 92.16.43c1.29 0 2.302.92 2.302 2.178 0 1.228-1.013 2.179-2.302 2.179-1.256 0-2.334-.951-2.334-2.179zm.555 5.063h3.563V22h-3.563V7.671zM110.856 13.962V22h-3.561v-7.701c0-2.27-1.383-3.437-2.979-3.437-1.628 0-3.652.89-3.652 3.65v7.486h-3.567V7.67h3.562v2.27c.737-1.78 2.979-2.577 4.452-2.577 3.688 0 5.745 2.424 5.745 6.598zM122.9 22l-5.589-6.505V22h-3.562V.063h3.562v12.18l4.115-4.572h4.637l-5.835 6.26L127.413 22H122.9zM6.617 17.527v-4.204L0 21.993h3.845l2.834-3.713c-.04-.249-.061-.5-.062-.753zM13.512 20.705a3.457 3.457 0 01-3.441-3.458c0-.027.009-10.947.009-12.716.003-1.627 1.376-3.103 3.155-3.103.796 0 1.564.3 2.147.843A4.488 4.488 0 0010.33.201a4.49 4.49 0 00-3.316 4.335v12.976c0 2.346 1.935 4.469 4.492 4.469a4.448 4.448 0 002.598-.826c.316-.22.603-.48.854-.773a3.398 3.398 0 01-1.446.323z"
          />
          <path
            fill="currentColor"
            d="M16.522 17.518v-4.205l-3.878 5.052a1.43 1.43 0 01-1.767.414 3.026 3.026 0 003.82 1.285 3.028 3.028 0 001.162-.876l.722-.938a4.413 4.413 0 01-.06-.732zM23.421 20.705a3.456 3.456 0 01-3.44-3.458l.008-12.716c0-1.625 1.377-3.1 3.155-3.1.795.001 1.561.302 2.144.844a4.489 4.489 0 00-5.05-2.07 4.49 4.49 0 00-3.319 4.331v12.976c0 2.346 1.935 4.469 4.492 4.469a4.449 4.449 0 002.598-.826c.316-.221.603-.48.855-.773a3.402 3.402 0 01-1.443.323z"
          />
          <path
            fill="currentColor"
            d="M23.944 16.519l-1.396 1.845a1.43 1.43 0 01-1.765.415 3.027 3.027 0 004.982.409l.616-.805-2.437-1.864zM23.14 1.85a2.769 2.769 0 00-2.44 1.468 1.43 1.43 0 011.847 2.117l-2.17 2.845v5.025l4.6-6.011a4.428 4.428 0 00.927-2.719 2.759 2.759 0 00-2.764-2.724zM13.235 1.85a2.768 2.768 0 00-2.438 1.468 1.43 1.43 0 012.157 1.196c.007.334-.102.66-.31.921L10.473 8.28v5.025l4.6-6.011a4.428 4.428 0 00.925-2.719 2.759 2.759 0 00-2.763-2.724z"
          />
        </g>
        <defs>
          <clipPath id="logo-clip">
            <path d="M0 0h127.417v22H0z" />
          </clipPath>
        </defs>
      </svg>
    </component>
  </div>
</template>

<style lang="scss" module>
  .wrapper {
    height: 60px;
    position: fixed;
    z-index: z-index('screen');
  }

  .toggle {
    transition: color $secondary-transition;

    &:focus {
      box-shadow: none;
    }

    :global(.is-sidebar-open) & {
      color: var(--color-white);
    }

    @include breakpoint('xl') {
      display: none;
    }
  }

  .link {
    overflow: hidden;
    width: 30px;

    @include breakpoint('md') {
      overflow: visible;
      width: auto;
    }

    @include breakpoint('xl') {
      margin-left: 14px;
    }
  }

  .logo {
    color: var(--color-purple);
    height: $logo-height;

    :global(.is-sidebar-open) & {
      color: var(--color-white);
    }

    @include breakpoint('xl') {
      color: var(--color-white);
    }
  }
</style>
